// Хуки для работы с picker Form2

import { useState, useCallback, useEffect, useMemo } from 'react';

// Хук для работы с picker справочников
export const usePicker = (
  handbooks,
  displayData,
  setLocalFormData,
  setFormData,
  getCombinedYarusy,
  yarusyDependent,
  setYarusyDependent,
  compositionItems,
  updateCompositionItem,
  setActiveYarusId
) => {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerField, setPickerField] = useState('');
  const [pickerOptions, setPickerOptions] = useState([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const [activeYarusId, setActiveYarusIdState] = useState(null);

  // Функция для открытия picker справочника
  const openPicker = useCallback((fieldName, handbookId, yarusId = null) => {
    let options = [];
    
    // Для обычных справочников используем стандартную логику
    const handbook = handbooks.find(h => h.id === handbookId);
    
    if (handbook && handbook.references) {
      options = handbook.references.map(r => ({
        value: r.id.toString(),
        label: r.name,
        description: r.description || null
      }));
    }
    
    if (options && options.length > 0) {
      let currentValue = '';
      
      // Проверяем, является ли поле полем яруса
      const isYarusField = ['klvozr', 'grvozr'].includes(fieldName);
      
      if (isYarusField && yarusId) {
        // Для полей ярусов берем значение из конкретного яруса (зависимые данные)
        const yarus = getCombinedYarusy().find(y => y.id === yarusId);
        currentValue = yarus ? yarus[fieldName] || '' : '';
        setActiveYarusIdState(yarusId);
        if (setActiveYarusId) {
          setActiveYarusId(yarusId);
        }
      } else if (fieldName === 'kodporodato' && yarusId) {
        // Для поля породы в модальном окне состава
        const item = compositionItems.find(item => item.id === yarusId);
        currentValue = item ? item.kodporodato || '' : '';
        setActiveYarusIdState(yarusId);
        if (setActiveYarusId) {
          setActiveYarusId(yarusId);
        }
      } else {
        // Для обычных полей используем стандартную логику
        currentValue = displayData[fieldName] || '';
        setActiveYarusIdState(null);
        if (setActiveYarusId) {
          setActiveYarusId(null);
        }
      }
      
      const selectedIndex = options.findIndex(opt => opt.value === currentValue);
      
      setPickerField(fieldName);
      setPickerOptions(options);
      setPickerSearch('');
      setSelectedOptionIndex(selectedIndex);
      setShowPicker(true);
    }
  }, [handbooks, displayData, getCombinedYarusy, compositionItems, setActiveYarusId]);

  // Функция для закрытия picker
  const closePicker = useCallback(() => {
    setShowPicker(false);
    setPickerField('');
    setPickerOptions([]);
    setPickerSearch('');
    setSelectedOptionIndex(-1);
    setActiveYarusIdState(null);
    if (setActiveYarusId) {
      setActiveYarusId(null);
    }
  }, [setActiveYarusId]);

  // Функция для выбора значения из picker
  const selectPickerValue = useCallback((value) => {
    // Находим полный объект опции
    const selectedOption = pickerOptions.find(opt => opt.value === value);
    
    // Сохраняем value (ID) для справочников
    const valueToSave = selectedOption ? selectedOption.value : value;
    
    // Проверяем, является ли поле полем яруса
    const isYarusField = ['klvozr', 'grvozr'].includes(pickerField);
    
    if (isYarusField && activeYarusId) {
      // Для полей ярусов обновляем зависимые данные
      const updatedYarusyDependent = yarusyDependent.map(yarus => 
        yarus.id === activeYarusId 
          ? { ...yarus, [pickerField]: valueToSave }
          : yarus
      );
      
      setYarusyDependent(updatedYarusyDependent);
    } else if (pickerField === 'kodporodato' && activeYarusId) {
      // Для поля породы в модальном окне состава
      updateCompositionItem(activeYarusId, 'kodporodato', valueToSave);
    } else {
      // Для обычных полей используем стандартную логику
      const newData = { 
        ...displayData, 
        [pickerField]: valueToSave
      };
      
      setLocalFormData(newData);
      if (setFormData) {
        setFormData(newData);
      }
    }
    
    closePicker();
  }, [pickerField, pickerOptions, activeYarusId, yarusyDependent, setYarusyDependent, updateCompositionItem, displayData, setLocalFormData, setFormData, closePicker]);

  // Мемоизированная функция для умной фильтрации с приоритетом по началу строки
  const filteredOptions = useMemo(() => {
    if (!pickerSearch || !pickerSearch.trim()) {
      return pickerOptions;
    }
    
    const searchLower = pickerSearch.toLowerCase();
    
    // Для поля породы используем умную фильтрацию
    if (pickerField === 'kodporodato') {
      // Предварительно вычисляем toLowerCase для всех элементов один раз
      const itemsWithLower = pickerOptions.map(item => ({
        ...item,
        _labelLower: item.label.toLowerCase(),
        _descriptionLower: item.description ? item.description.toLowerCase() : null
      }));
      
      // Фильтруем
      const filtered = itemsWithLower.filter(item => 
        item._labelLower.includes(searchLower) ||
        (item._descriptionLower && item._descriptionLower.includes(searchLower))
      );
      
      // Сортируем: сначала те, что начинаются с поискового запроса, потом остальные
      return filtered.sort((a, b) => {
        const aStartsWith = a._labelLower.startsWith(searchLower);
        const bStartsWith = b._labelLower.startsWith(searchLower);
        
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        
        // Если оба начинаются или оба не начинаются, сортируем по алфавиту
        return a._labelLower.localeCompare(b._labelLower);
      }).map(({ _labelLower, _descriptionLower, ...item }) => item); // Убираем временные поля
    }
    
    // Для остальных полей используем обычную фильтрацию
    return pickerOptions.filter(option => {
      const labelLower = option.label.toLowerCase();
      const descLower = option.description ? option.description.toLowerCase() : null;
      return labelLower.includes(searchLower) || (descLower && descLower.includes(searchLower));
    });
  }, [pickerOptions, pickerSearch, pickerField]);

  // Функция для фильтрации опций по поиску
  const getFilteredOptions = useCallback(() => {
    return filteredOptions;
  }, [filteredOptions]);

  // Функция для получения индекса выбранного элемента в отфильтрованном списке
  const getSelectedIndexInFiltered = useCallback(() => {
    const filtered = getFilteredOptions();
    if (selectedOptionIndex >= 0 && selectedOptionIndex < pickerOptions.length) {
      const selectedOption = pickerOptions[selectedOptionIndex];
      return filtered.findIndex(opt => opt.value === selectedOption.value);
    }
    return -1;
  }, [getFilteredOptions, selectedOptionIndex, pickerOptions]);

  // Эффект для прокрутки к выбранному элементу в picker
  useEffect(() => {
    if (showPicker) {
      const timer = setTimeout(() => {
        const filteredIndex = getSelectedIndexInFiltered();
        if (filteredIndex >= 0) {
          const selectedElement = document.querySelector(`.picker-list li:nth-child(${filteredIndex + 1})`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showPicker, selectedOptionIndex, getFilteredOptions, getSelectedIndexInFiltered, pickerOptions, pickerSearch]);

  return {
    showPicker,
    pickerField,
    pickerOptions,
    pickerValue: displayData[pickerField] || '',
    pickerSearch,
    selectedOptionIndex,
    openPicker,
    closePicker,
    selectPickerValue,
    getFilteredOptions,
    getSelectedIndexInFiltered,
    setPickerSearch
  };
};

